import { Button, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  LOIN_CODE_CATALOG_INITIAL_VALUES,
  LoinCodeCatalogInitialValuesType,
} from "../../../../../constants/initial-schemas";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useAddCataLogCode } from "../../../../../queries/catalog/use-add-catalog-code";
import { useUpdateCataLogCode } from "../../../../../queries/catalog/use-update-catalog-code";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
} from "../../../../../utils/utils";
import Input from "../../../../Input";
import SideSheetDrawer from "../../../../SidesheetDrawer";

interface LoincCodeCatalogDialogProps {
  dialogTitle: string;
  initialValues?: LoinCodeCatalogInitialValuesType;
  disclosureHost: DisclosureHost;
  uuid?: string;
}

const loincCodeCatalogValidationSchema = Yup.object({
  loinc_code: Yup.string().trim().required().label("LOINC Code"),
  description: Yup.string().trim().required().label("Description"),
});

const LoincCodeCatalogDrawer = ({
  dialogTitle,
  initialValues,
  disclosureHost,
  uuid,
}: LoincCodeCatalogDialogProps) => {
  const {
    handleBlur,
    values,
    handleChange,
    handleSubmit,
    resetForm,
    errors,
    touched,
  } = useFormik({
    initialValues: initialValues ?? LOIN_CODE_CATALOG_INITIAL_VALUES,
    validationSchema: loincCodeCatalogValidationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const { mutateAsync: addLoincCode } = useAddCataLogCode();
  const { mutateAsync: editLoincCode } = useUpdateCataLogCode();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();
  function onSubmit(values: LoinCodeCatalogInitialValuesType) {
    const transformedValues = {
      code: values.loinc_code.trim(),
      description: values.description.trim(),
    };

    (initialValues
      ? editLoincCode({
          values: { ...transformedValues, uuid, type: "LOINC" },
        })
      : addLoincCode({ values: { ...transformedValues, type: "LOINC" } })
    )
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));

        queryClient.invalidateQueries({
          queryKey: ["catalog-diagnosis-code-import"],
        });

        resetAndClose();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  }

  function resetAndClose() {
    resetForm();
    disclosureHost.close();
  }

  return (
    <SideSheetDrawer
      open={disclosureHost.opened}
      title={dialogTitle}
      onClose={() => {
        resetForm();
        disclosureHost.close();
      }}
      w="50vw"
      footerActions={
        <Button
          sx={{ width: "105px" }}
          onClick={() => handleSubmit()}
          variant="contained"
        >
          {dialogTitle?.includes("Edit") ? "Edit" : "Add"}
        </Button>
      }
    >
      <Grid container spacing={2} width="100%">
        <Grid item xs={12}>
          <Input
            required
            label="LOINC Code"
            textFieldProps={{
              name: "loinc_code",
              value: values.loinc_code,
              onChange: handleChange,
              onBlur: handleBlur,
              placeholder: "Enter Code",
            }}
            error={touched.loinc_code && errors.loinc_code}
          />
        </Grid>
        <Grid item xs={12}>
          <Input
            required
            label="Description"
            textFieldProps={{
              name: "description",
              value: values.description,
              onChange: handleChange,
              onBlur: handleBlur,
              placeholder: "Description",
              multiline: true,
              rows: 4,
            }}
            error={touched.description && errors.description}
          />
        </Grid>
      </Grid>
    </SideSheetDrawer>
  );
};
export default LoincCodeCatalogDrawer;
